import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { Overlay } from 'features/OverlayFunction/models/overlay';
import overlayService from 'features/OverlayFunction/overlayService';
import { overlayActions } from 'features/OverlayFunction/overlaySlice';
import { useEffect } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import * as yup from 'yup';
import './style.scss';

type FormValues = Overlay;

export interface OverlayDetailModalProps {
  overlayId?: number;
  onClose: (isDatachanged: boolean) => void;
}

const OverlayDetailModal = ({ overlayId, onClose }: OverlayDetailModalProps) => {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  // selectọrs
  const isEditMode = useAppSelector(selectIsEditMode);

  const refectchOverlayList = () => {
    dispatch(overlayActions.fetchOverlayList());
  };

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('OverlayFunction.Warn.warn_field_required')),
    // 非表示にする最大来店人数: 0〜9の整数（空欄は0扱い） / Max guest count to hide (0-9, blank=0)
    hide_max_guest_count: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' || originalValue === null ? 0 : value))
      .typeError(t('OverlayFunction.Warn.warn_number_required'))
      .integer(t('OverlayFunction.Warn.warn_integer'))
      .min(0, t('OverlayFunction.Warn.warn_range'))
      .max(9, t('OverlayFunction.Warn.warn_range'))
      .default(0),
  });

  // form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {},
    mode: 'onSubmit',
  });

  const fetchData = async (id: number) => {
    const res = await overlayService.getDetail(id);
    const data = res.data;
    const formattedData = {
      ...data,
      guide_ja: convertBreakLine(data?.guide_ja),
      guide_en: convertBreakLine(data?.guide_en),
      guide_vi: convertBreakLine(data?.guide_vi),
      guide_ko: convertBreakLine(data?.guide_ko),
      guide_zh_hant: convertBreakLine(data?.guide_zh_hant),
      guide_zh_hans: convertBreakLine(data?.guide_zh_hans),
      remarks: convertBreakLine(data?.remarks),
    };
    reset({
      ...formattedData,
    });
  };

  useEffect(() => {
    if (!overlayId) return;
    fetchData(overlayId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayId]);

  const handleClose = () => {
    onClose?.(false);
  };

  const formartBreakLine = (text: string | undefined) => {
    return text?.replace(/\n/g, '<br>');
  };

  const convertBreakLine = (text: string | undefined) => {
    return text?.replace(/<br>/g, '\n');
  };

  const processAddNewOverlay = async (data: FormValues) => {
    try {
      const response = await overlayService.create(data);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      refectchOverlayList();
      onClose?.(true);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };

  const processUpdateOverlay = async (id: number, data: FormValues) => {
    try {
      const response = await overlayService.update(id, data);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      refectchOverlayList();
      onClose?.(true);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };

  const onSubmit = (data: FormValues) => {
    const formattedData = {
      ...data,
      guide_ja: formartBreakLine(data?.guide_ja),
      guide_en: formartBreakLine(data?.guide_en),
      guide_vi: formartBreakLine(data?.guide_vi),
      guide_ko: formartBreakLine(data?.guide_ko),
      guide_zh_hant: formartBreakLine(data?.guide_zh_hant),
      guide_zh_hans: formartBreakLine(data?.guide_zh_hans),
      remarks: formartBreakLine(data?.remarks),
    };

    if (overlayId) {
      processUpdateOverlay(overlayId, formattedData);
    } else {
      processAddNewOverlay(formattedData);
    }
  };

  return (
    <Modal show backdrop="static" dialogClassName="overlay-detail-modal" size="lg" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('OverlayFunction.Title.detail_head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-between my-3">
            <div className="pe-3">
              <div className="ps-3">{t('OverlayFunction.Form.guide_description')}</div>
            </div>
            <div className="ps-3">
              <Button type="submit" className="btn btn--dark-green">
                {t('OverlayFunction.Button.save')}
              </Button>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-start mt-3"></div>
          <div className="sticky-table flex-grow-1 flex-shrink-1 mt-2">
            <Table bordered className="form-course">
              <tbody>
                <tr>
                  <th colSpan={3}>{t('OverlayFunction.Form.overlay_name')}</th>
                  <td>
                    <Form.Control className="text-center" {...register('name')} />
                    {errors?.name && (
                      <span className="small text-danger d-inline-block mt-1">{errors?.name?.message}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th rowSpan={6} colSpan={2} style={{ width: '25%' }}>
                    {t('OverlayFunction.Form.guide')}
                  </th>
                  <th style={{ width: '25%' }}>{t('OverlayFunction.Form.japanese')}</th>
                  <td colSpan={3} style={{ width: '50%' }}>
                    <Form.Control as="textarea" {...register('guide_ja')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('OverlayFunction.Form.english')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" {...register('guide_en')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('OverlayFunction.Form.simplified_chinese')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" {...register('guide_zh_hans')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('OverlayFunction.Form.traditional_chinese')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" {...register('guide_zh_hant')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('OverlayFunction.Form.korean')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" {...register('guide_ko')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th>{t('OverlayFunction.Form.vietnamese')}</th>
                  <td colSpan={3}>
                    <Form.Control as="textarea" {...register('guide_vi')}></Form.Control>
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('OverlayFunction.Form.remarks')}</th>
                  <td>
                    <Form.Control as="textarea" {...register('remarks')} />
                  </td>
                </tr>
                <tr>
                  <th colSpan={3}>{t('OverlayFunction.Form.hide_max_guest_count')}</th>
                  <td>
                    <Form.Control
                      type="number"
                      min={0}
                      max={9}
                      className="text-center"
                      {...register('hide_max_guest_count', { valueAsNumber: true })}
                    />
                    {errors?.hide_max_guest_count && (
                      <span className="small text-danger d-inline-block mt-1">
                        {errors?.hide_max_guest_count?.message}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default OverlayDetailModal;
